import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import Wordmark from "../components/Wordmark";

const BG = "https://images.unsplash.com/photo-1489846986031-7cea03ab8fd0?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset. Please sign in.");
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4">
      <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-[var(--auth-overlay)]" />
      <div className="relative z-10 w-full max-w-md fade-up">
        <Link to="/login" className="mb-8 flex justify-center" data-testid="auth-brand">
          <Wordmark sizeClass="text-4xl" />
        </Link>
        <div className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-surface)]/95 p-8 shadow-xl backdrop-blur-xl" data-testid="reset-form">
          <h1 className="font-head text-3xl tracking-wide text-[var(--text-primary)]">Set New Password</h1>

          {!token ? (
            <div data-testid="reset-no-token">
              <p className="mt-3 text-sm text-[var(--text-secondary)]">This reset link is missing or invalid. Please request a new one.</p>
              <Link to="/forgot-password"
                className="mt-6 block w-full rounded-[var(--card-radius)] bg-[var(--accent)] py-3 text-center font-bold uppercase tracking-wide text-[var(--accent-contrast)] transition-colors hover:bg-[var(--accent-hover)]"
                data-testid="reset-request-new">
                Request New Link
              </Link>
            </div>
          ) : (
            <form onSubmit={submit}>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">Choose a new password for your account.</p>

              {error && <div className="mt-4 rounded-md border border-[var(--border-accent)] bg-[var(--accent)]/10 px-3 py-2 text-sm text-[var(--accent-hover)]" data-testid="reset-error">{error}</div>}

              <label className="mt-5 block text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">New Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="mt-1 w-full rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-base)] px-4 py-3 text-[var(--text-primary)] outline-none focus:border-[var(--accent)]"
                data-testid="reset-password-input" placeholder="••••••••" />

              <label className="mt-4 block text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Confirm Password</label>
              <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
                className="mt-1 w-full rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-base)] px-4 py-3 text-[var(--text-primary)] outline-none focus:border-[var(--accent)]"
                data-testid="reset-confirm-input" placeholder="••••••••" />

              <button type="submit" disabled={loading}
                className="mt-6 w-full rounded-[var(--card-radius)] bg-[var(--accent)] py-3 font-bold uppercase tracking-wide text-[var(--accent-contrast)] transition-colors hover:bg-[var(--accent-hover)] disabled:opacity-60"
                data-testid="reset-submit-button">
                {loading ? "Resetting..." : "Reset Password"}
              </button>

              <p className="mt-5 text-center text-sm text-[var(--text-secondary)]">
                <Link to="/login" className="font-semibold text-[var(--text-primary)] hover:underline" data-testid="reset-go-login">Back to Sign In</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
